import swal from 'sweetalert2'

import { DashboardModule, Form } from '@domain-group/suit-theme'

import { generateRedirectUrl } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import { isAgencyUser, isBrandUser, isProviderUser } from '@functions/user'

import { generateUrls as generateAgencyUrls } from '@hooks/useAgency'
import { generateUrls as generateBrandUrls } from '@hooks/useBrand'
import { generateUrls as generateProviderUrls } from '@hooks/useProvider'
import { UseUserPayload, useUserForm } from '@hooks/useUser'

import EntitySelector from '@components/EntitySelector'

import type { AgencyModel, BrandModel, ProviderModel } from '@models/types'
import type { CurrentUserPayload } from '@hooks/useCurrentUser'

const handleEntitySelect = (
  entity: AgencyModel | BrandModel | ProviderModel | null,
  updateUser: (userPayload: HandleCallbackActionParams) => void,
  updateFn: () => void,
) => {
  if (!entity){
    swal
      .fire({
        confirmButtonColor: '#DD6B55',
        confirmButtonText: 'Ok',
        icon: 'warning',
        text: 'Another association must be assigned for this user type.',
        title: 'Association Removed',
      })
    return
  }

  const userPayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: { association_id: entity?.id },
    requestOptions: { include_user_association_id: true },
    toastText: 'User Association Updated',
  }

  // @ts-expect-error TS2345
  updateUser(userPayload)
}

interface UserAssociationProps {
  currentUserPayload: CurrentUserPayload,
  updateUser: (userPayload: HandleCallbackActionParams) => void,
  userForm: ReturnType<typeof useUserForm>,
  userPayload: UseUserPayload,
}

const UserAssociation = (props: UserAssociationProps) => {
  const {
    currentUserPayload,
    updateUser,
    userForm,
    userPayload,
  } = props

  const {
    entityState: {
      association_id,
    },
  } = userForm

  const {
    callbacks: {
      updateUser: updateFn,
    },
    // @ts-expect-error TS2339
    role: { title },
  } = userPayload

  const { isBrandUser: currentUserIsBrandUser, isProviderUser: currentUserIsProviderUser } = currentUserPayload

  // @ts-expect-error TS2339
  const { editAgencyUrl } = isAgencyUser(title) ? generateAgencyUrls({ id: association_id }) : ''
  // @ts-expect-error TS2339
  const { editBrandUrl } = isBrandUser(title) ? generateBrandUrls({ id: association_id }) : ''
  // @ts-expect-error TS2339
  const { editProviderUrl } = isProviderUser(title) ? generateProviderUrls({ id: association_id }) : ''

  return (
    <DashboardModule title="Association">
      <Form padding="large">
        <Form.Field>
          {isAgencyUser(title) && (
            <EntitySelector
              callbacks={{
                // @ts-expect-error TS2345
                selectItem: agency => handleEntitySelect(agency, updateUser, updateFn),
              }}
              entityKey="agencies"
              goToHref={`${editAgencyUrl}?redirect=${generateRedirectUrl()}`}
              label="Agency"
              selectedItemId={association_id}
            />
          )}

          {isBrandUser(title) && (
            <EntitySelector
              callbacks={{
                // @ts-expect-error TS2345
                selectItem: brand => handleEntitySelect(brand, updateUser, updateFn),
              }}
              disabled={currentUserIsBrandUser}
              entityKey="brands"
              goToHref={`${editBrandUrl}?redirect=${generateRedirectUrl()}`}
              label="Brand"
              selectedItemId={association_id}
            />
          )}

          {isProviderUser(title) && (
            <EntitySelector
              callbacks={{
                // @ts-expect-error TS2345
                selectItem: provider => handleEntitySelect(provider, updateUser, updateFn),
              }}
              disabled={currentUserIsProviderUser}
              entityKey="providers"
              goToHref={`${editProviderUrl}?redirect=${generateRedirectUrl()}`}
              label="Provider"
              selectedItemId={association_id}
            />
          )}
        </Form.Field>
      </Form>
    </DashboardModule>
  )
}

export default UserAssociation
